import classNames from 'classnames';
import CurrencyFormat from 'components/CurrencyFormat';
import DurationFormat from 'components/DurationFormat';
import InlineMessage from 'components/InlineMessage';
import React from 'react';
import { useMatch } from 'react-router-dom';
import type { Currency } from 'types/account';
import type { CostAndDurationSummary } from 'utils/summary';
import styles from './ReportTotals.module.scss';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  billedState: boolean | null;
  currency?: Currency;
  isLoading?: boolean;
  isMultiCurrency?: boolean;
  showMoney?: boolean;
  totals: CostAndDurationSummary;
}

type ValueTuple = [string, number, number];

const ReportTotals: React.FC<Props> = ({
  billedState,
  className,
  currency,
  isLoading,
  isMultiCurrency,
  showMoney = true,
  totals,
  ...props
}) => {
  const values: Array<ValueTuple | false> = [
    billedState === null && ['Total logged', totals.minutesLogged, totals.costLogged],
    billedState !== false && ['Billed', totals.minutesBilled, totals.costBilled],
    billedState !== true && ['Unbilled', totals.minutesUnbilled, totals.costUnbilled],
  ];

  const groups = values.filter(Boolean) as Array<ValueTuple>;

  const isUserReport = !!useMatch('/:account_id/users/:user_id/report');

  return (
    <div className={classNames(styles.totals, className)} {...props}>
      {groups.map(([label, hours, money]) => (
        <div key={label} className={styles.group} data-testid={`report-totals-${label}`}>
          <span className={styles.label}>{label}</span>
          <div className={classNames(styles.numbers, { [styles.loading]: isLoading })}>
            <DurationFormat value={hours} format="h[h] mm[m]" animated={!isLoading} />
            {showMoney && !isMultiCurrency && (
              <>
                {', '}
                <CurrencyFormat
                  className={styles.money}
                  value={money}
                  animated={!isLoading}
                  currency={currency ?? totals.currency}
                />
              </>
            )}
          </div>
        </div>
      ))}
      {isMultiCurrency && (
        <InlineMessage type="warning" className={styles.multiCurrencyWarning}>
          This report contains projects with multiple currencies, so money sums and some money
          widgets cannot be displayed.
          {!isUserReport && (
            <>
              <br />
              To see money sums, change the project filter to only include projects with the same
              currency.
            </>
          )}
        </InlineMessage>
      )}
    </div>
  );
};

export default ReportTotals;
